/**
 * @file host-normalize.ts
 * @description Нормализация адреса (хоста) магазина — phase19.28 этап А
 * (v0.19.63; проект данных docs/phases/phase19.28-materials/db-design-domains-a.md
 * §1 «Нормализация» и §3 «Как API узнаёт магазин по адресу»; правила
 * docs/business/platform.md PL4–PL5 и свои домены).
 *
 * Зачем в shared: один и тот же адрес приводят к виду из базы и API (поле
 * «Свой домен» в кабинете платформы, `resolvePublicShop` по заголовку Host),
 * и витрина (`proxy.ts` перед запросом `shop-by-host`). Если правила
 * разойдутся, адрес, сохранённый кабинетом, не найдётся при входе покупателя.
 *
 * Без зависимостей от Node: `url.domainToASCII` в браузере нет, поэтому
 * punycode получаем через WHATWG `new URL("http://" + host).hostname` —
 * он же приводит к нижнему регистру (в том числе кириллицу по UTS 46).
 */

import { isDevLocalhostHost } from "./dev-localhost.js";

/**
 * Хост в базе. ДОЛЖНА СОВПАДАТЬ ДОСЛОВНО с SQL-ограничением
 * `ShopDomain_host_check` (db-design-domains-a §1):
 *   CHECK (char_length(host) <= 253 AND host ~
 *     '^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,63}|xn--[a-z0-9-]{1,59})$')
 * Меняется только вместе с миграцией CHECK — иначе код пропустит адрес,
 * который база отвергнет (500 вместо понятной ошибки), или наоборот.
 */
export const SHOP_DOMAIN_HOST_PATTERN = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;

/** Предел длины хоста — `char_length(host) <= 253` в том же CHECK. */
export const SHOP_DOMAIN_HOST_MAX_LENGTH = 253;

/** Сырой ввод длиннее этого не разбираем вовсе: защита от мусора в заголовке Host. */
const RAW_INPUT_MAX_LENGTH = 2048;

/** Вид адреса магазина (enum `ShopDomainKind` в базе). */
export type ShopDomainKind = "SUBDOMAIN" | "CUSTOM";

/** Почему адрес не годится. */
export type NormalizeHostError =
  | "host_empty"
  | "host_invalid"
  | "host_ip"
  | "host_single_label"
  | "host_localhost"
  | "host_too_long"
  | "host_platform"
  | "host_custom_inside_platform"
  | "host_subdomain_outside_platform";

/** Подсказки по-русски — текст ошибки API и поля в кабинете. */
export const NORMALIZE_HOST_ERROR_MESSAGES: Readonly<Record<NormalizeHostError, string>> = Object.freeze({
  host_empty: "Укажите адрес сайта.",
  host_invalid: "Адрес записан неверно: буквы, цифры, дефисы и точки, например shop.ru.",
  host_ip: "Нужно доменное имя, а не IP-адрес.",
  host_single_label: "Укажите адрес целиком, с зоной: например shop.ru.",
  host_localhost: "Адрес localhost нельзя привязать к магазину.",
  host_too_long: "Адрес слишком длинный.",
  host_platform: "Это адрес самой платформы, его нельзя привязать к магазину.",
  host_custom_inside_platform: "Адреса внутри домена платформы выдаются только как поддомен магазина.",
  host_subdomain_outside_platform: "Поддомен магазина должен быть внутри домена платформы.",
});

export interface NormalizeHostOptions {
  /** Вид адреса: для CUSTOM запрещено всё внутри `platformBaseDomain`, для SUBDOMAIN — всё вне. */
  kind?: ShopDomainKind;
  /** Базовый домен платформы (`PLATFORM_BASE_DOMAIN`, напр. `commora.ru`). */
  platformBaseDomain?: string;
  /** Адреса самой платформы (`PLATFORM_HOSTS`): кабинет, API, лендинг. */
  platformHosts?: readonly string[];
  /** dev: пропускать `localhost` и `<slug>.localhost` (§3 п. 3 — только вне production). */
  allowLocalhost?: boolean;
}

export type NormalizeHostResult = { data: string; error: null } | { data: null; error: NormalizeHostError };

const fail = (error: NormalizeHostError): NormalizeHostResult => ({ data: null, error });

/** Схема (`https://`, `HTTP://`) или протокол-относительное `//` в начале. */
const SCHEME_PREFIX = /^(?:[a-z][a-z0-9+.-]*:)?\/\//i;

/** Последняя метка — число (или 0x…): WHATWG принял бы это за IPv4 (`127.1` → `127.0.0.1`). */
const NUMERIC_LAST_LABEL = /(?:^|\.)(?:\d+|0x[0-9a-f]*)$/i;

const IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;

/**
 * Лёгкая чистка без проверки правил: схема, путь, запрос, якорь, `user@`,
 * порт, завершающая точка, нижний регистр ASCII. Отдельно — чтобы так же
 * чистить списки платформенных адресов из окружения.
 */
export function stripHostDecorations(input: string): string {
  let value = input.trim().replace(SCHEME_PREFIX, "");
  // Путь, запрос, якорь; обратная косая — WHATWG считает её разделителем пути.
  const pathAt = value.search(/[/?#\\]/);
  if (pathAt >= 0) value = value.slice(0, pathAt);
  // Учётные данные: всё до последнего `@` — не адрес.
  const atAt = value.lastIndexOf("@");
  if (atAt >= 0) value = value.slice(atAt + 1);
  // Порт снимаем только у обычного имени: у IPv6 двоеточий несколько.
  if (!value.startsWith("[") && value.indexOf(":") === value.lastIndexOf(":")) {
    value = value.replace(/:\d*$/, "");
  }
  return value.replace(/\.$/, "").toLowerCase();
}

/** Адрес в ASCII (punycode) через WHATWG; null — URL его не принял. */
function toAsciiHost(value: string): string | null {
  try {
    return new URL(`http://${value}`).hostname.replace(/\.$/, "");
  } catch {
    return null;
  }
}

/** Внутри домена (`x.base`) или сам домен. */
function isSameOrInside(host: string, base: string): boolean {
  return host === base || host.endsWith(`.${base}`);
}

/**
 * Привести адрес к виду, в котором он хранится в `ShopDomain.host`, или
 * объяснить отказ кодом. Порядок проверок важен: сначала распознаём IP и
 * localhost, чтобы человек увидел точную причину, а не общее «неверно».
 */
export function normalizeHost(input: string, options: NormalizeHostOptions = {}): NormalizeHostResult {
  if (input.length > RAW_INPUT_MAX_LENGTH) return fail("host_too_long");
  const cleaned = stripHostDecorations(input);
  if (cleaned === "") return fail("host_empty");
  // Двоеточие после снятия порта или скобки — это IPv6.
  if (cleaned.includes(":") || cleaned.startsWith("[")) return fail("host_ip");
  if (NUMERIC_LAST_LABEL.test(cleaned)) return fail("host_ip");
  // WHATWG пропускает `*`, а маска домена магазину не положена.
  if (cleaned.includes("*")) return fail("host_invalid");

  const host = toAsciiHost(cleaned);
  if (host === null || host === "") return fail("host_invalid");
  if (IPV4.test(host)) return fail("host_ip");

  if (host === "localhost" || host.endsWith(".localhost")) {
    if (!options.allowLocalhost) return fail("host_localhost");
    // dev-резолв: только `localhost` и `<slug>.localhost`; регуляркой базы не проверяем —
    // такие адреса в базу не пишутся.
    return isDevLocalhostHost(host) ? { data: host, error: null } : fail("host_invalid");
  }
  if (!host.includes(".")) return fail("host_single_label");
  if (host.length > SHOP_DOMAIN_HOST_MAX_LENGTH) return fail("host_too_long");
  if (!SHOP_DOMAIN_HOST_PATTERN.test(host)) return fail("host_invalid");

  const base = options.platformBaseDomain ? stripHostDecorations(options.platformBaseDomain) : "";
  const platformHosts = (options.platformHosts ?? []).map(stripHostDecorations).filter((h) => h !== "");
  if ((base !== "" && host === base) || platformHosts.includes(host)) return fail("host_platform");
  if (base !== "") {
    if (options.kind === "CUSTOM" && isSameOrInside(host, base)) return fail("host_custom_inside_platform");
    if (options.kind === "SUBDOMAIN" && !isSameOrInside(host, base)) return fail("host_subdomain_outside_platform");
  }
  return { data: host, error: null };
}
